import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { SystemSettingKey, systemSettingsApi } from "@/api";
import { folderLeafOf } from "@/common/components/mail-merge-access/MailMergeAccess.utils";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface FormValues {
  dataSourceFolder: string;
}

type ValidationType = { [Property in keyof FormValues]?: Yup.AnySchema };

const SETTING_KEY = SystemSettingKey.MailMergeDataSourceFolder;
// Mirrors SystemSettingValueSpecs.MaxMailMergeDataSourceFolderLength (Windows MAX_PATH).
const MAX_FOLDER_PATH_LENGTH = 260;
const RESERVED_FOLDER_NAME_CHARS = [":", "*", "?", '"', "<", ">", "|"];
// The Unicode Cc category, which is what char.IsControl covers on the backend: DEL and the C1 range
// are control characters too, not only the ones below space.
const CONTROL_CHARACTER = /\p{Cc}/u;

/**
 * Mirrors SystemSettingValueRules.FolderPath so a value the backend refuses shows as a field error
 * rather than coming back as a 400 with a generic toast: the value is a whole folder path, of which
 * only the last segment has to be a usable folder name — that leaf is all the browser ever reports
 * back about a released folder. Windows drops a trailing dot or space from a folder name, so a leaf
 * carrying one could never be matched; the dot covers "." and ".." too.
 *
 * Surrounding whitespace is the one rule not mirrored — the backend rejects it, this form trims it
 * away on submit instead of making the user do it.
 */
function endsInAFolderName(folderPath: string): boolean {
  const leaf = folderLeafOf(folderPath);

  if (leaf === null || leaf.endsWith(".") || leaf.endsWith(" ")) {
    return false;
  }

  if (CONTROL_CHARACTER.test(leaf)) {
    return false;
  }

  for (const character of leaf) {
    if (RESERVED_FOLDER_NAME_CHARS.includes(character)) {
      return false;
    }
  }

  return true;
}

export function useMailMergeSettings() {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const { data: setting, isLoading } = useQuery({
    queryKey: [QueryKey.SystemSetting, SETTING_KEY],
    queryFn: () => systemSettingsApi.getSystemSetting({ key: SETTING_KEY }),
  });

  const updateMutation = useMutation({
    mutationFn: (value: string) =>
      systemSettingsApi.updateSystemSetting({
        key: SETTING_KEY,
        updateSystemSettingRequest: { value },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.SystemSetting, SETTING_KEY] });
      showSuccess(t("settings.mailMerge.saveSuccess"));
    },
  });

  const initialValues = useMemo<FormValues>(
    () => ({
      dataSourceFolder: typeof setting?.value === "string" ? setting.value : "",
    }),
    [setting]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape<ValidationType>({
        dataSourceFolder: Yup.string()
          .trim()
          .required(() => t("common.validation.fieldRequired", { fieldName: t("settings.mailMerge.dataSourceFolder") }))
          .max(MAX_FOLDER_PATH_LENGTH, ({ max }) => t("common.validation.maxLength", { max }))
          .test("ends-in-a-folder-name", t("settings.mailMerge.invalidFolderPath"), endsInAFolderName),
      }),
    [t]
  );

  const formik = useFormikWithDirtyForm<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await updateMutation.mutateAsync(values.dataSourceFolder.trim());
    },
  });

  return {
    formik,
    isLoading,
    folderPathMaxLength: MAX_FOLDER_PATH_LENGTH,
  };
}
